import { useMemo, useState } from "react";
import { SurveyStatus } from "@kalyzee/kast-web-sdk/websocket";
import { Icons, SurveyAnswerCard, StarRating, SurveyStudentHeader, ReactIcons } from "@kalyzee/escaet-ui";
import { Button } from "@kalyzee/kast-web-sdk/web-components";
import { useMeetingSelector, useMultiMeetingAPI } from "../../hooks/meeting";

import styles from "./SurveyStudentContainer.module.css";
import { sdkClient } from "../../helpers/sdk";
import { toastError } from "../../helpers/toast";
import { SurveyResultsViewer } from "./SurveyResultsViewer";

export const SurveyStudentContainer = () => {
  const { focusedMeetingAPI } = useMultiMeetingAPI();

  // multi survey not supported yet, we take the last pushed survey
  const [currentSurvey] = useMeetingSelector(focusedMeetingAPI, state => state.surveys[state.surveys.length - 1]);
  
  const [choice, setChoice] = useState<{ id: string, questionId: string, value: string }>();

  const confirmedChoice = useMemo(() => {
    if (!currentSurvey?.questions?.length) return undefined;
    return currentSurvey.questions[0].choices.find(choice => choice.selfVote); // Only one vote supported for now
  }, [currentSurvey]);

  
  let selectedChoice: { id: string, value: string } | undefined;
  if (confirmedChoice) {
    selectedChoice = { id: confirmedChoice.id, value: confirmedChoice.content };
  } else if (choice) {
    selectedChoice = { id: choice.id, value: choice.value };
  }

  const currentQuestionResults = useMemo(() => {
    if (!currentSurvey?.questions) return undefined;
    const question = currentSurvey.questions[0];
    if (!question) return undefined; // Only one question supported for now
    const questionVoteNb = question.choices.reduce((acc, val) => {
      if (val.votesNumber) return acc + val.votesNumber;
      return acc;
    }, 0);
    return {
      totalVotes: questionVoteNb,
      choices: question.choices.map(choice => {
        return {
          id: choice.id,
          content: choice.content,
          votesNumber: choice.votesNumber ?? 0,
        };
      })
    };
  }, [currentSurvey]);
  
  if (!currentSurvey) return null;

  const onAnswerSurvey = (id: string, questionId: string, value: string) => {
    setChoice({ id, questionId, value });
  }

  const onAnswerStarsSurvey = async (rating: number) => {
    const ratingStr = rating.toString();
    const choice = currentSurvey.questions[0].choices.find(choice => choice.content === ratingStr)?.id ?? '';
    if (!choice) return;
    setChoice({ id: choice, questionId: currentSurvey.questions[0].id, value: ratingStr });
  };

  const onSendAnswer = async () => {
    const websocket = sdkClient.getWebsocketClient();
    if (!websocket || !focusedMeetingAPI || !choice) return;

    const result = await websocket.emitter.survey.answerCreate({
      surveyId: currentSurvey.id,
      questions: [
        {
          id: choice.questionId,
          choiceIds: [
            choice.id || '',
          ],
        },
      ]
    });
    if (result.error || !result.response) {
      toastError("Impossible d'envoyer votre réponse");
      return;
    } 

    // So the store is updated immediately (i don't receive answer events as an user)
    focusedMeetingAPI.store.dispatch("surveyAnswer", { answer: result.response, isSelf: true });
  };

  const renderButton = () => {
    if (confirmedChoice) {
      return (
        <div className={styles.loadingButton}>
          En attente des résultats 
          <ReactIcons.Spinner3 style={{ opacity: 0.5, width: 24 }} />
        </div>
      );
    }
    return (
      <Button
        className={`${styles.submitButton} ${selectedChoice ? "" : styles.disabled}`}
        onClick={() => onSendAnswer()}
        title="Envoyer ma réponse"
      />
    );
  }

  const renderContent = () => {
    if (currentSurvey.status === SurveyStatus.Open) {
      return (
        <>
          <div className={styles.header}>
            <SurveyStudentHeader
              title="Répondre au sondage"
              subtitle={currentSurvey.name}
              icon={Icons.Hand}
              startDate={currentSurvey.createdAt} 
            />
          </div>
          <div style={{
            flex: 1,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}>
            {currentSurvey.questions.map((question) => (
              <div className={styles.choicesContainer}>
                {currentSurvey.name === "Étoiles" && (
                  <div className={styles.starsContainer}>
                    <StarRating 
                      allowHalfStars
                      value={selectedChoice?.value ? parseFloat(selectedChoice.value) : 0} 
                      onChange={onAnswerStarsSurvey} 
                      showHoverPreview={!confirmedChoice}
                      containerStyle={{ gap: 50 }}
                      starStyle={{ width: 100, height: 100  }}
                    />
                  </div>
                )}
                {currentSurvey.name !== "Étoiles" && (
                  <div className={styles.answerCardGrid}>
                    {question.choices.map((choice, index) => (
                      <SurveyAnswerCard
                        key={index}
                        value={choice.content}
                        onClick={() => onAnswerSurvey(choice.id, question.id, choice.content)}
                        isSelected={choice.id === selectedChoice?.id} 
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className={styles.submitButtonContainer}>
            {renderButton()}
          </div>
        </>
      )
    } else {
      return (
        <SurveyResultsViewer 
          survey={currentSurvey}
          customColors={{
            normal: [
              {
                background: "#115990",
                fill: "#80C7FF",
                text: "#FFFFFF",
              },
            ],
            stars: {
              background: "#115990",
              fill: "#80C7FF",
              text: "#FFFFFF",
            },
          }}
        />
      );
      // return (
      //   <div style={{
      //     display: "flex",
      //     flexDirection: "column",
      //     width: "100%",
      //   }}>
      //     <div style={{
      //       gap: "1em",
      //       display: "flex",
      //       flexDirection: "row",
      //       justifyContent: "center",
      //       width: "100%",
      //     }}>
      //       {currentQuestionResults?.choices.map((choice, idx) => (
      //         <div 
      //           style={{ 
      //             width: 150,
      //             fontSize: 40,
      //             lineHeight: "35px",
      //             fontWeight: "bold",
      //             textAlign: "center",
      //             alignSelf: "center",
      //             color: "white",
      //             marginBottom: "0.5em",
      //           }}
      //           key={`title-${idx}`}
      //         >
      //           {choice.content}
      //         </div>
      //       ))}
      //     </div>
      //     <div style={{ 
      //       display: "flex",
      //       flexDirection: "row",
      //       justifyContent: "center",
      //       gap: "1em",
      //       width: "100%",
      //     }}>
      //       {currentQuestionResults?.choices.map((choice, idx) => (
      //         <StudentResult 
      //           className={styles.resultBar}
      //           key={idx}
      //           total={currentQuestionResults.totalVotes}
      //           number={choice.votesNumber}
      //         />
      //       ))}
      //     </div>
      //   </div>
      // );
    }
  }

  return (
    <div className={styles.overlay}>
      {renderContent()}
    </div>
  );
};
